import i18nFactory from '@yandex-int/i18n';
import { MutationOptions, WatchQueryOptions } from 'apollo-client';
import ApolloClient from 'apollo-client/ApolloClient';
import { OperationVariables } from 'apollo-client/core/types';
import { FetchResult } from 'apollo-link';
import { forEach, get } from 'lodash';

import { getMutationIdMutation } from '../../graphql/mutations/grid/grid';
import { checkClientMutationState } from '../../graphql/queries/grid/common';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { getErrorMessage } from '../../service/GraphqlErrorHelper/GraphqlErrorHelper';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { getNetworkError } from '../../service/NetworkErrors/NetworkErrors';
import { ErrorResponseType, ValidationResultItem } from '../../typings/common';
import {
    CheckClientMutationStateQuery,
    CheckClientMutationStateQueryVariables,
    GdClientMutationState,
    getMutationId,
    getMutationIdVariables
} from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';

import * as keyset from './DNAApolloClient.i18n';

const gqlI18N = i18nFactory(keyset);

const MUTATION_REQUEST_LIMIT = 2;

interface AwaitMutationOptions extends MutationOptions {
    login: string;
}

interface NetworkErrorReponse {
    statusCode: number;
    result?: {
        code: number;
        description?: string;
        success: boolean;
        text: string;
    };
}

interface GraphlQLErrorResponse {
    graphQLErrors: GraphQLError[];
    networkError: NetworkErrorReponse;
}

export interface ErrorLoadData {
    type: ErrorResponseType;
    message: string;
    code?: string;
}

type GraphQLError = PlainError | ValidationError;

interface PlainError {
    message: string;
    type?: string;
    code?: string;
}

interface ValidationError {
    [key: string]: {
        validationResult?: {
            errors: ValidationResultItem[];
            warnings: ValidationResultItem[];
        };
    };
}

export type RequestOptions = WatchQueryOptions | MutationOptions | AwaitMutationOptions;

const notPoliteFallback = <T>(data: T) => {
    let validationErrorExist = false;

    const errors = Object.keys(data).map((name: string) => {
        const mutationResult: { validationResult?: object } = data[name];

        validationErrorExist = validationErrorExist || Boolean(mutationResult.validationResult);

        return {
            [name]: {
                validationResult: mutationResult.validationResult
            }
        };
    });

    if (validationErrorExist) {
        throw {
            graphQLErrors: errors
        };
    }
};

export class DNAApolloClient<NormalizedCacheObject> extends ApolloClient<NormalizedCacheObject> {
    public async query<T>(options: WatchQueryOptions) {
        const optionsCopy = { ...options };

        try {
            return await super.query<T>(options);
        } catch (error) {
            if (error.networkError && error.networkError.statusCode === 403) {
                try {
                    return await super.query<T>(optionsCopy);
                } catch (e) {
                    throw this.processError(e, optionsCopy);
                }
            } else {
                throw this.processError(error, optionsCopy);
            }
        }
    }

    public async awaitMutate<T>(
        options: AwaitMutationOptions
    ): Promise<
        FetchResult<
            T & {
                notSynced?: boolean;
            }
        >
    > {
        const optionsCopy = { ...options };

        const additionalOptions = get(optionsCopy.variables, 'additionalOptions');

        return new Promise(async (resolve, reject) => {
            try {
                const result: FetchResult<T> = await super.mutate<T>(optionsCopy);

                if (get(additionalOptions, 'notPoliteMutation')) {
                    notPoliteFallback(result.data);
                }

                this.setMutationQueue<T>(resolve, reject, result, optionsCopy.login);
            } catch (e) {
                reject(this.processError(e, options));
            }
        });
    }

    public async getClientMutationId(input: getMutationIdVariables): Promise<FetchResult<getMutationId>> {
        return this.mutate<getMutationId>({
            mutation: getMutationIdMutation,
            variables: input
        });
    }

    public async awaitLastMutation(login: string) {
        return new Promise(async (resolve, reject) => {
            try {
                const clientMutationIdResult = await this.getClientMutationId({
                    login
                });

                this.setMutationQueue(resolve, reject, clientMutationIdResult, login);
            } catch (e) {
                reject(this.processError(e));
            }
        });
    }

    public async mutate<T, TVariables = OperationVariables>(
        options: MutationOptions<T, TVariables>
    ): Promise<FetchResult<T>> {
        const optionsCopy = { ...options };

        const additionalOptions = get(optionsCopy.variables, 'additionalOptions');

        try {
            const result = await super.mutate(optionsCopy);

            if (get(additionalOptions, 'notPoliteMutation')) {
                notPoliteFallback(result.data);
            }

            return result;
        } catch (error) {
            throw this.processError(error, options);
        }
    }

    private processError(e: GraphlQLErrorResponse, requestOptions?: RequestOptions) {
        const errors = e.graphQLErrors;

        const responseNetworkError = e.networkError;
        const statusCode =
            responseNetworkError &&
            ((responseNetworkError.result && responseNetworkError.result.code) || responseNetworkError.statusCode);

        const networkError = statusCode ? { message: getNetworkError(statusCode) } : null;

        if (!errors.length && !networkError) {
            return new ApiMethodExecutingError({ message: gqlI18N('request-error') });
        }

        const messageErrors = errors
            .map(
                (el: PlainError) =>
                    getErrorMessage({
                        code: el.code || '',
                        path: '',
                        params: {}
                    }) || el.message
            )
            .filter(Boolean);

        const { mutationErrors, mutationWarnings } = errors
            .filter((el: PlainError) => !el.message)
            .reduce(
                (
                    acc: {
                        mutationErrors: ValidationResultItem[];
                        mutationWarnings: ValidationResultItem[];
                    },
                    item: ValidationError
                ) => {
                    forEach(item, prop => {
                        if (prop.validationResult) {
                            acc.mutationErrors = acc.mutationErrors.concat(prop.validationResult.errors || []);
                            acc.mutationWarnings = acc.mutationWarnings.concat(prop.validationResult.warnings || []);
                        }
                    });

                    return acc;
                },
                {
                    mutationErrors: [],
                    mutationWarnings: []
                }
            );

        let errorText, errorType;

        if (mutationErrors.length || mutationWarnings.length) {
            const firstError: ValidationResultItem = mutationErrors[0] || mutationWarnings[0];

            errorText = getErrorMessage(firstError);
        }

        if (networkError) {
            errorText = networkError.message;
            errorType = ErrorResponseType.network;
        } else {
            errorText = errorText || messageErrors.join(' ');
            errorType = ErrorResponseType.graphQL;
        }

        return new ApiMethodExecutingError({
            message: errorText,
            errors: mutationErrors,
            warnings: mutationWarnings,
            errorType,
            requestOptions
        });
    }

    private async setMutationQueue<T = {}>(
        resolve: (value?: {}) => void,
        reject: (e?: {}) => void,
        result: FetchResult<
            T & {
                getClientMutationId?: {
                    mutationId: string;
                };
            }
        >,
        login: string
    ) {
        if (!result.data || !result.data.getClientMutationId) {
            resolve(result);
            return;
        }

        const clientMutationId = result.data.getClientMutationId.mutationId;

        let limit = MUTATION_REQUEST_LIMIT;

        while (true) {
            try {
                if (!limit) {
                    showNoticeMessage(NoticeType.error, gqlI18N('mutation-warning'));
                    reject(new NotSyncedError(result));

                    return;
                }

                const status = await this.checkMutationState({
                    input: {
                        mutationId: clientMutationId,
                        login: login
                    }
                });

                if (status === GdClientMutationState.SYNCED) {
                    resolve(result);
                    return;
                }

                limit--;
            } catch (e) {
                reject(e);
                return;
            }
        }
    }

    private checkMutationState(variables: CheckClientMutationStateQueryVariables): Promise<GdClientMutationState> {
        return this.query<CheckClientMutationStateQuery>({
            query: checkClientMutationState,
            fetchPolicy: 'network-only',
            variables
        }).then(result => {
            return result.data.checkClientMutationState.state;
        });
    }
}
